import { useCallback, useState } from "react";
import { supabase } from "@/store/shared/supabase";

export type ChangePasswordResult = { ok: true } | { ok: false; error: string };

/**
 * Changement de mot de passe réel via Supabase Auth.
 *
 * 1. Le mot de passe actuel est vérifié en ré-authentifiant l'utilisateur
 *    (Supabase ne l'exige pas nativement sur updateUser).
 * 2. Le nouveau mot de passe est ensuite appliqué avec `auth.updateUser`.
 *
 * Aucun mot de passe n'est stocké côté application : tout passe par Supabase Auth.
 */
export function useChangePassword() {
  const [saving, setSaving] = useState(false);

  const changePassword = useCallback(
    async (email: string, current: string, next: string): Promise<ChangePasswordResult> => {
      setSaving(true);
      try {
        if (email) {
          const { error: verifyError } = await supabase.auth.signInWithPassword({
            email: email.trim(),
            password: current,
          });
          if (verifyError) {
            return { ok: false, error: "Le mot de passe actuel est incorrect." };
          }
        }

        const { error } = await supabase.auth.updateUser({ password: next });
        if (error) {
          return { ok: false, error: "La mise à jour du mot de passe a échoué. Réessayez." };
        }
        return { ok: true };
      } catch {
        return { ok: false, error: "La mise à jour du mot de passe a échoué. Réessayez." };
      } finally {
        setSaving(false);
      }
    },
    []
  );

  return { changePassword, saving };
}